import { Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { DecisionFilters, HomeApiService } from "./home-api.service";
import { DecisionAdapter } from "./adapters/decision.adapter";
import { HomeIaLabelMappingService } from "./home-ia-label-mapping.service";
import { ActivityAdapter } from "../../components/home/adapters/activity.adapter";
import { DatesAdapter } from "../../components/home/adapters/dates.adapter";
import { MetricsAdapter } from "../../components/home/adapters/metrics.adapter";
import { PulseAdapter } from "../../components/home/adapters/pulse.adapter";
import {
  ActivityHistoryPage,
  DecisionActionRequest,
  DecisionActionResult,
  DecisionDetail,
  DecisionPage,
  DocumentPulse,
  HomeMetric,
  DetectedDatesSummary,
  SystemActivity,
  NextBestDecision
} from "../../models/home";

@Injectable({ providedIn: "root" })
export class HomeDataService {
  private readonly maxDecisionPageSize = 50;

  constructor(
    private readonly homeApiService: HomeApiService,
    private readonly labelMapping: HomeIaLabelMappingService
  ) {}

  getDocumentPulse(): Observable<DocumentPulse> {
    return this.homeApiService.getPulse().pipe(map((dto) => PulseAdapter.toViewModel(dto)));
  }

  getDetectedDatesSummary(): Observable<DetectedDatesSummary> {
    return this.homeApiService
      .getDatesSummary()
      .pipe(map((dto) => this.mapDatesSummary(DatesAdapter.toSummaryViewModel(dto.roles ?? []))));
  }

  getDecisions(page = 1, pageSize = 20, filters?: DecisionFilters): Observable<DecisionPage> {
    return this.homeApiService
      .getDecisions(page, pageSize, filters)
      .pipe(map((dto) => DecisionAdapter.toPageViewModel(dto)));
  }

  getAllDecisions(filters?: DecisionFilters, pageSize = this.maxDecisionPageSize): Observable<DecisionPage> {
    const resolvedPageSize = Math.min(Math.max(1, pageSize), this.maxDecisionPageSize);

    return this.getDecisions(1, resolvedPageSize, filters).pipe(
      switchMap((firstPage) => {
        if (firstPage.totalPages <= 1) {
          return of(firstPage);
        }

        const remainingPages = Array.from({ length: firstPage.totalPages - 1 }, (_, index) => index + 2);

        return forkJoin(remainingPages.map((page) => this.getDecisions(page, resolvedPageSize, filters))).pipe(
          map((pages) => this.mergeDecisionPages(firstPage, pages))
        );
      })
    );
  }

  getNextBestDecision(filters?: DecisionFilters): Observable<NextBestDecision | null> {
    return this.homeApiService
      .getNextDecision(filters)
      .pipe(map((dto) => DecisionAdapter.toNextBestDecisionViewModel(dto)));
  }

  getDecisionDetail(decisionId: string): Observable<DecisionDetail> {
    return this.homeApiService
      .getDecisionDetail(decisionId)
      .pipe(map((dto) => DecisionAdapter.toDetailViewModel(dto)));
  }

  executeDecisionAction(
    decisionId: string,
    request: DecisionActionRequest,
    idempotencyKey?: string
  ): Observable<DecisionActionResult> {
    return this.homeApiService
      .executeDecisionAction(decisionId, DecisionAdapter.toActionRequestDto(request), idempotencyKey)
      .pipe(map((dto) => DecisionAdapter.toActionResultViewModel(dto)));
  }

  getLastActivity(): Observable<SystemActivity> {
    return this.homeApiService
      .getLastActivity()
      .pipe(map((dto) => ActivityAdapter.toViewModel(dto)));
  }

  getActivityFeed(): Observable<readonly SystemActivity[]> {
    return this.homeApiService.getActivity().pipe(
      map((dto) => (dto.items ?? []).map((item) => ActivityAdapter.toViewModel(item)))
    );
  }

  getActivityHistory(page = 1, pageSize = 6): Observable<ActivityHistoryPage> {
    if (page === 1) {
      return this.getActivityFeed().pipe(
        map((items) => this.buildActivityHistoryPage(items, page, pageSize))
      );
    }

    return this.homeApiService.getActivityHistory(page, pageSize).pipe(
      map((dto) => {
        const items = (dto.items ?? []).map((item) => ActivityAdapter.toViewModel(item));
        return this.buildActivityHistoryPage(items, dto.page ?? page, dto.pageSize ?? pageSize, dto.total, dto.totalPages);
      })
    );
  }

  getMetrics(): Observable<readonly HomeMetric[]> {
    return this.homeApiService.getMetrics().pipe(map((dto) => MetricsAdapter.toViewModel(dto)));
  }

  private buildActivityHistoryPage(
    items: readonly SystemActivity[],
    page: number,
    pageSize: number,
    totalItems?: number,
    totalPages?: number
  ): ActivityHistoryPage {
    const resolvedTotalItems = totalItems ?? items.length;
    const resolvedTotalPages = totalPages ?? Math.max(1, Math.ceil(resolvedTotalItems / pageSize));

    return {
      items,
      page,
      pageSize,
      totalItems: resolvedTotalItems,
      totalPages: resolvedTotalPages,
      hasMore: page < resolvedTotalPages
    };
  }

  private mergeDecisionPages(firstPage: DecisionPage, remainingPages: readonly DecisionPage[]): DecisionPage {
    const mergedItems = [
      ...firstPage.items,
      ...remainingPages.flatMap((page) => page.items)
    ];
    const latestRefreshedAt = [...remainingPages].reverse().find((page) => !!page.refreshedAt)?.refreshedAt;

    return {
      ...firstPage,
      items: mergedItems,
      page: 1,
      pageSize: mergedItems.length || firstPage.pageSize,
      totalItems: Math.max(firstPage.totalItems, mergedItems.length),
      totalPages: 1,
      hasMore: false,
      refreshedAt: latestRefreshedAt ?? firstPage.refreshedAt
    };
  }

  private mapDatesSummary(summary: DetectedDatesSummary): DetectedDatesSummary {
    const roleLabels: Record<string, string> = {};
    Object.keys(summary.byRole ?? {}).forEach((role) => {
      const mapped = this.labelMapping.mapRole(role, summary.roleLabels?.[role] ?? null);
      if (mapped) {
        roleLabels[role] = mapped;
      }
    });

    const monitoredDates = (summary.monitoredDates ?? []).map((date) => {
      const mappedRole = this.labelMapping.mapRole(date.role, date.roleLabel ?? null);
      const mappedReason = this.labelMapping.mapReasonCode(date.triggerReason ?? null, null);
      return {
        ...date,
        roleLabel: mappedRole ?? undefined,
        triggerReason: mappedReason ?? ""
      };
    });

    return {
      ...summary,
      roleLabels,
      monitoredDates
    };
  }

}
